import { ArrowForward } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useEventAccess } from '../../../../../hooks/useEventAccess';
import { QAComponentInterface, UiComponentElementInterface } from '../../../../../interfaces/pagebuilder';
import { setPostRegistrationAction } from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { sendQAQuestionService } from '../../../../../services/services';
import { ConditionalWrapper } from '../../../../common/ConditionalWrapper';
import VyTooltip from '../../../../common/VyTooltip';
import { TitleAndDescription } from '../../../pageBuilder/TitleAndDescription';

interface FormProps {
  question: string;
}

const QA = ({ uiComponent }: UiComponentElementInterface<QAComponentInterface>) => {
  const event = useSelector((state) => state.common.event);
  const user = useSelector((state) => state.common.user);
  const pagedata = useSelector((state) => state.common.pagedata);

  const borderRadius = event?.template?.customBorderRadius && parseInt(event?.template?.customBorderRadius) >= 0 ? event?.template?.customBorderRadius : '4px';
  const { t } = useTranslation('event');
  const { handleSubmit, control, reset, watch } = useForm<FormProps>();
  const router = useRouter();
  const dispatch = useDispatch();
  const { loadUserData } = useEventAccess();

  const useMBWStyle = event?.organization.supportedPagebuilderThemes.includes('mbw');

  const [disabled, setDisabled] = useState(false);

  const submit = async (props: FormProps) => {
    if (!user) {
      if (event?.modules.guestmanagement.settings.authUserAfterSignupWhenPossible !== false)
        router.push(`${event?.eventauth.registerPageUrl}?redirect=${btoa(unescape(encodeURIComponent(router.asPath)))}`);
      else
        dispatch(
          setPostRegistrationAction(() => {
            loadUserData();
          })
        );
      return;
    }
    setDisabled(true);

    try {
      await sendQAQuestionService({
        pageid: pagedata?._id || '',
        question: props.question,
      });
    } catch (error) {
      console.error(error);
    }

    reset({ question: '' });
    setTimeout(
      () => {
        setDisabled(false);
      },
      uiComponent?.delay ? uiComponent.delay * 1000 : 0
    );
  };

  return (
    <div
      className={`w-full relative`}
      style={{
        color: uiComponent?.styling?.textColor || '#000',
      }}>
      <TitleAndDescription component={uiComponent!} />
      <ConditionalWrapper condition={!user} wrapper={(children) => <VyTooltip title={t('event:qa-registration-required')}>{children}</VyTooltip>}>
        <>
          {uiComponent?.label && <p className="">{uiComponent.label}</p>}
          <form className="w-full flex flex-row space-x-4" onSubmit={handleSubmit(submit)}>
            <Controller
              control={control}
              name="question"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="outlined"
                  disabled={disabled}
                  sx={{
                    '& fieldset': { borderColor: event?.template.mainColor || 'white' },
                    '& fieldset:hover': { borderColor: 'white' },
                  }}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: borderRadius,
                    height: '100%',
                  }}
                  inputProps={{
                    maxlength: uiComponent?.maxLength,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <small>
                          {watch('question')?.length ?? 0} / {uiComponent?.maxLength}
                        </small>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <IconButton
              type="submit"
              style={{
                background: !watch('question') ? 'grey' : event?.template.mainColor,
                borderRadius: borderRadius,
                ...(useMBWStyle ? { width: '64px' } : {}),
              }}
              disabled={disabled || !watch('question')}
              size="large">
              <ArrowForward style={{ color: !watch('question') ? 'white' : event?.template.mainButtonTextColor }} />
            </IconButton>
          </form>
        </>
      </ConditionalWrapper>
    </div>
  );
};

export default QA;
